import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { BUILDMATE_COLOR_PALETTE, TRANSPARENT_VALUE } from '../constants/colorPalette';

type ColorSwatchPickerProps = {
  value: string;
  onChange: (color: string) => void;
  allowTransparent?: boolean;
  /** Opacity slider (#RRGGBBAA). Metin / arka plan yarı saydamlığı için. */
  allowOpacity?: boolean;
  /** Etiket (örn. "Metin rengi") */
  label?: string;
  className?: string;
};

const PALETTE_GAP = 4;
const Z_OVERLAY = 9998;
const Z_POPOVER = 9999;

type ParsedColor = { rgb: string; alpha: number };

/** #RGB / #RRGGBB / #RRGGBBAA (isteğe bağlı #) → rgb+#rrggbb + alpha 0–1; geçersizse null. */
function parseColorValue(raw: string): ParsedColor | null {
  const s = raw.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(s)) {
    return {
      rgb: `#${s[0]}${s[0]}${s[1]}${s[1]}${s[2]}${s[2]}`.toLowerCase(),
      alpha: 1,
    };
  }
  if (/^[0-9a-fA-F]{6}$/.test(s)) {
    return { rgb: `#${s}`.toLowerCase(), alpha: 1 };
  }
  if (/^[0-9a-fA-F]{8}$/.test(s)) {
    const a = (parseInt(s.slice(6, 8), 16) || 0) / 255;
    return { rgb: `#${s.slice(0, 6)}`.toLowerCase(), alpha: a };
  }
  return null;
}

/** alpha 0–1 → #rrggbb veya #rrggbbaa (tam opaksa 6 hane). */
function formatColor(rgb: string, alpha: number): string {
  const base = rgb.startsWith('#') ? rgb.toLowerCase() : `#${rgb}`.toLowerCase();
  const a = Math.max(0, Math.min(1, alpha));
  if (a >= 1 - 1 / 255) return base;
  const hex = Math.round(a * 255)
    .toString(16)
    .padStart(2, '0');
  return `${base}${hex}`;
}

function hexDisplayValue(value: string): string {
  if (value === TRANSPARENT_VALUE) return '';
  return value.startsWith('#') ? value : value ? `#${value}` : '';
}

/** Projeye özel renk seçici: palet + hızlı hex girişi. Palet portal ile body'de açılır, kesilmez. */
const ColorSwatchPicker: React.FC<ColorSwatchPickerProps> = ({
  value,
  onChange,
  allowTransparent = false,
  allowOpacity = false,
  label,
  className,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<{ top: number; left: number } | null>(null);
  const [hexDraft, setHexDraft] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const customInputRef = useRef<HTMLInputElement>(null);
  const hexInputRef = useRef<HTMLInputElement>(null);

  const isTransparent = value === TRANSPARENT_VALUE;
  const parsed = !isTransparent ? parseColorValue(value) : null;
  const displayValue = parsed ? formatColor(parsed.rgb, allowOpacity ? parsed.alpha : 1) : undefined;
  const opacityPct = Math.round((parsed?.alpha ?? 1) * 100);

  useEffect(() => {
    if (!open) {
      setPlacement(null);
      setHexDraft(null);
      return;
    }
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setPlacement({ left: rect.left, top: rect.bottom + PALETTE_GAP });
    setHexDraft(hexDisplayValue(value));

    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const panel = panelRef.current;
        if (!panel) return;
        const r = trigger.getBoundingClientRect();
        const panelHeight = panel.offsetHeight;
        const spaceBelow = window.innerHeight - r.bottom;
        const openAbove = spaceBelow < panelHeight + PALETTE_GAP && r.top >= panelHeight + PALETTE_GAP;
        setPlacement(prev =>
          prev ? { ...prev, top: openAbove ? r.top - panelHeight - PALETTE_GAP : r.bottom + PALETTE_GAP } : null
        );
        hexInputRef.current?.focus();
        hexInputRef.current?.select();
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [open]);

  // Paletten / dışarıdan değer değişince hex alanını senkron tut (yazarken dokunma).
  useEffect(() => {
    if (!open || hexDraft === null) return;
    if (document.activeElement === hexInputRef.current) return;
    setHexDraft(hexDisplayValue(value));
  }, [value, open]);

  const handlePick = (color: string) => {
    if (color === TRANSPARENT_VALUE) {
      onChange(color);
      setOpen(false);
      return;
    }
    const next = parseColorValue(color);
    if (!next) {
      onChange(color);
      setOpen(false);
      return;
    }
    // Paletten seçince mevcut opacity'yi koru (allowOpacity açıksa).
    const alpha = allowOpacity && parsed ? parsed.alpha : 1;
    onChange(formatColor(next.rgb, alpha));
    setOpen(false);
  };

  const commitHex = (raw: string, close = false) => {
    const normalized = parseColorValue(raw);
    if (!normalized) {
      setHexDraft(hexDisplayValue(value));
      return;
    }
    const out = formatColor(normalized.rgb, allowOpacity ? normalized.alpha : 1);
    onChange(out);
    setHexDraft(out);
    if (close) setOpen(false);
  };

  const handleOpacityChange = (pct: number) => {
    if (!parsed) return;
    const alpha = Math.max(0, Math.min(100, pct)) / 100;
    onChange(formatColor(parsed.rgb, alpha));
  };

  const handleCustomClick = () => {
    customInputRef.current?.click();
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v) {
      const next = parseColorValue(v);
      const alpha = allowOpacity && parsed ? parsed.alpha : 1;
      onChange(next ? formatColor(next.rgb, alpha) : v);
      setOpen(false);
    }
    e.target.value = '';
  };

  const popoverContent =
    open && placement && typeof document !== 'undefined' ? (
      <>
        <div
          className="fixed inset-0"
          style={{ zIndex: Z_OVERLAY }}
          aria-hidden
          onClick={() => setOpen(false)}
        />
        <div
          ref={panelRef}
          className="fixed z-[9999] p-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl w-[11.5rem]"
          style={{
            top: placement.top,
            left: placement.left,
            zIndex: Z_POPOVER,
          }}
        >
          <div className="mb-2">
            <label className="sr-only">{t('colorPicker.hexLabel')}</label>
            <input
              ref={hexInputRef}
              type="text"
              spellCheck={false}
              autoComplete="off"
              placeholder={allowOpacity ? '#000000ff' : '#000000'}
              value={hexDraft ?? hexDisplayValue(value)}
              onChange={e => {
                const raw = e.target.value;
                setHexDraft(raw);
                const normalized = parseColorValue(raw);
                if (normalized) {
                  onChange(formatColor(normalized.rgb, allowOpacity ? normalized.alpha : 1));
                }
              }}
              onBlur={e => commitHex(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitHex((e.target as HTMLInputElement).value, true);
                }
                e.stopPropagation();
              }}
              onClick={e => e.stopPropagation()}
              className="w-full px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-mono tracking-wide"
              title={
                allowOpacity
                  ? t('colorPicker.hexHintOpacity')
                  : t('colorPicker.hexHint')
              }
            />
          </div>
          {allowOpacity && !isTransparent && parsed && (
            <div className="mb-2">
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="text-[10px] font-medium text-gray-600 dark:text-gray-400">{t('colorPicker.opacity')}</label>
                <span className="text-[10px] font-mono tabular-nums text-gray-500 dark:text-gray-400">
                  {opacityPct}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={opacityPct}
                onChange={e => handleOpacityChange(Number(e.target.value))}
                onClick={e => e.stopPropagation()}
                className="w-full h-1.5 accent-primary-600 cursor-pointer"
                title={t('colorPicker.opacity')}
              />
            </div>
          )}
          <div className="grid grid-cols-5 gap-1.5">
            {allowTransparent && (
              <button
                type="button"
                onClick={() => handlePick(TRANSPARENT_VALUE)}
                className={clsx(
                  'w-7 h-7 rounded-md border-2 flex-shrink-0',
                  isTransparent
                    ? 'border-primary-500 ring-1 ring-primary-500'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-400'
                )}
                title={t('colorPicker.transparent')}
              >
                <span className="w-full h-full rounded bg-[length:6px_6px] bg-[linear-gradient(45deg,#999_25%,transparent_25%,transparent_75%,#999_75%),linear-gradient(-45deg,#999_25%,transparent_25%,transparent_75%,#999_75%)]" />
              </button>
            )}
            {BUILDMATE_COLOR_PALETTE.map(hex => (
              <button
                key={hex}
                type="button"
                onClick={() => handlePick(hex)}
                className={clsx(
                  'w-7 h-7 rounded-md border-2 flex-shrink-0 transition-colors hover:ring-2 hover:ring-primary-400',
                  parsed?.rgb.toLowerCase() === hex.toLowerCase() && !isTransparent
                    ? 'border-primary-500 ring-2 ring-primary-500'
                    : 'border-gray-200 dark:border-gray-600'
                )}
                style={{ backgroundColor: hex }}
                title={hex}
              />
            ))}
            <button
              type="button"
              onClick={handleCustomClick}
              className="w-7 h-7 rounded-md border-2 border-dashed border-gray-400 dark:border-gray-500 flex items-center justify-center flex-shrink-0 hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-primary-900/20 text-gray-500 dark:text-gray-400 text-xs"
              title={t('colorPicker.customColor')}
            >
              +
            </button>
          </div>
          <input
            ref={customInputRef}
            type="color"
            className="absolute opacity-0 w-0 h-0 pointer-events-none"
            onChange={handleCustomChange}
          />
        </div>
      </>
    ) : null;

  return (
    <div className={clsx('relative', className)}>
      {label ? (
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{label}</label>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={clsx(
          'w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-colors',
          'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-900'
        )}
        title={
          isTransparent
            ? t('colorPicker.transparent')
            : displayValue
              ? allowOpacity && parsed && parsed.alpha < 1
                ? `${parsed.rgb} · ${opacityPct}%`
                : displayValue
              : t('colorPicker.transparent')
        }
      >
        {isTransparent ? (
          <span className="w-6 h-6 rounded border border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-800 bg-[length:8px_8px] bg-[linear-gradient(45deg,#ccc_25%,transparent_25%,transparent_75%,#ccc_75%),linear-gradient(-45deg,#ccc_25%,transparent_25%,transparent_75%,#ccc_75%)]" />
        ) : (
          <span
            className="w-6 h-6 rounded border border-gray-400/50 bg-[length:6px_6px] bg-[linear-gradient(45deg,#ccc_25%,transparent_25%,transparent_75%,#ccc_75%),linear-gradient(-45deg,#ccc_25%,transparent_25%,transparent_75%,#ccc_75%)]"
          >
            <span
              className="block w-full h-full rounded"
              style={{ backgroundColor: displayValue || '#ffffff' }}
            />
          </span>
        )}
      </button>
      {typeof document !== 'undefined' && createPortal(popoverContent, document.body)}
    </div>
  );
};

export default ColorSwatchPicker;
